import React, { useEffect, useMemo, useState } from 'react';
import { generatePath, useHistory } from 'react-router-dom';

import { fetchCapabilitiesForExtension } from 'flow/actions/app-actions';
import {
    deleteAvatarImage,
    fetchEditableContact,
    updateAvatarImage,
    updateEditableContact,
    updateExternalEditableContact,
} from 'flow/actions/contact-actions';
import ContactEditFields from 'flow/components/contact-components/ContactEditFields';
import ContactEditAvatar from 'flow/components/headers/ContactEditAvatar';
import BaseModal from 'flow/components/ui-components/BaseModal';
import { useModal } from 'flow/contexts/ModalProvider';
import useAppContext from 'flow/hooks/useAppContext';
import useCustomer from 'flow/hooks/useCustomer';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import { getContact } from 'flow/selectors/contact-selectors';
import {
    makeSelectExtensionKeyFromContactKey,
    selectUserCountryCode,
} from 'flow/selectors/ExtensionSelectors';
import { LocationType } from 'flow/types/Location';
import { routes } from 'flow/types/routes';
import { TypedMap } from 'flow/types/TypedMap';
import { getContactAvatar, isEmptyNullOrUndefined } from 'flow/utils';
import { isFlowFreeCustomer } from 'flow/utils/flow-utils';
import DEFAULT_AVATAR_SVG from 'images/ic_telavox_default_avatar_60.svg';

import EditContactModalLoadingSkeleton from './EditContactModalLoadingSkeleton';

interface Props {
    contact?: TypedMap<any>;
    isEditingOwnContact?: boolean;
}

const EditContactModal = ({ contact, isEditingOwnContact }: Props) => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();
    const history = useHistory<LocationType>();
    const { ownCustomer } = useCustomer();
    const { openModal, closeModal } = useModal();

    const ownCountryCode = useAppSelector(selectUserCountryCode);

    const contactKey = contact?.get('key');

    const selectExtensionKey = useMemo(
        makeSelectExtensionKeyFromContactKey,
        [],
    );
    const extensionKey = useAppSelector((state) =>
        contactKey ? selectExtensionKey(state, contactKey) : undefined,
    );

    const editableContact = useAppSelector(
        (state) =>
            contact?.get('editableContactDTO') ||
            getContact(state, contactKey, extensionKey)?.get(
                'editableContactDTO',
            ),
    );

    const [interimContactDTO, setInterimContactDTO] = useState(editableContact);

    const contactSrc = getContactAvatar(contact);
    const editableSrc = getContactAvatar(editableContact);
    const avatarSrc = editableSrc || contactSrc;
    const [interimAvatarSrc, setInterimAvatarSrc] = useState<string>(avatarSrc);
    const [interimAvatarFile, setInterimAvatarFile] = useState<File>(null);

    const contactFields = interimContactDTO?.get('contactFields');

    useEffect(() => {
        if (extensionKey) {
            dispatch(fetchCapabilitiesForExtension(extensionKey));
        }
    }, [dispatch, extensionKey]);

    useEffect(() => {
        if (!editableContact && contactKey) {
            dispatch(fetchEditableContact(contactKey));
        }
    }, [dispatch, contactKey, editableContact]);

    useEffect(() => {
        if (!interimContactDTO && editableContact) {
            setInterimContactDTO(editableContact);
        }
    }, [editableContact, interimContactDTO]);

    const isDeletable = extensionKey
        ? isFlowFreeCustomer(ownCustomer) && contact.get('editable')
        : contact.get('deletable');

    const handleUpdateAvatar = () => {
        if (
            interimAvatarSrc === DEFAULT_AVATAR_SVG &&
            avatarSrc !== DEFAULT_AVATAR_SVG
        ) {
            dispatch(deleteAvatarImage(contactKey, extensionKey));
        }
        if (
            interimAvatarSrc !== DEFAULT_AVATAR_SVG &&
            interimAvatarSrc !== avatarSrc
        ) {
            dispatch(
                updateAvatarImage(contactKey, interimAvatarFile, extensionKey),
            );
        }
    };

    const handleSave = () => {
        dispatch(
            extensionKey
                ? updateEditableContact(
                      interimContactDTO,
                      extensionKey,
                      handleUpdateAvatar,
                  )
                : updateExternalEditableContact(
                      interimContactDTO,
                      handleUpdateAvatar,
                  ),
        );
    };

    const handleConfirmDialog = () =>
        openModal('deleteContact', {
            extensionKey,
            contact,
        });

    const updateEditableContactString = (
        evt: React.ChangeEvent<HTMLInputElement>,
    ) => {
        const newTarget = interimContactDTO.updateIn(
            ['contactFields', evt.target.id, 'value'],
            () => evt.target.value,
        );

        if (interimContactDTO !== newTarget) {
            setInterimContactDTO(newTarget);
        }
    };

    const updateEditableContactNumber = (id: string, phoneNumber: string) => {
        const newTarget = interimContactDTO.setIn(
            ['contactFields', id, 'number', 'number'],
            isEmptyNullOrUndefined(phoneNumber) ? '' : phoneNumber,
        );

        if (interimContactDTO !== newTarget) {
            setInterimContactDTO(newTarget);
        }
    };

    const isLoading = !interimContactDTO;

    return (
        <BaseModal
            open={true}
            header={i18next.t('ContactCard.EditContact')}
            onSubmit={isLoading ? undefined : handleSave}
            onRemove={
                isLoading ? undefined : isDeletable && handleConfirmDialog
            }
            disableSubmit={isLoading}
            openConfirmation={true}
        >
            {isLoading ? (
                <EditContactModalLoadingSkeleton />
            ) : (
                <div className="flex flex-col gap-y-6">
                    <ContactEditAvatar
                        avatarSource={interimAvatarSrc}
                        setAvatarSource={setInterimAvatarSrc}
                        setInterimAvatarFile={setInterimAvatarFile}
                    />
                    <ContactEditFields
                        contactFields={contactFields}
                        updateContactString={updateEditableContactString}
                        updateContactNumber={updateEditableContactNumber}
                        userCountryCode={ownCountryCode}
                        showEmailEditHint={isEditingOwnContact}
                        extensionKey={extensionKey}
                        navigateAuthenticationSettings={
                            isEditingOwnContact
                                ? () => {
                                      closeModal();
                                      history.push(
                                          generatePath(
                                              routes[
                                                  'settings.account.authentication'
                                              ],
                                              { extensionKey },
                                          ),
                                      );
                                  }
                                : null
                        }
                    />
                </div>
            )}
        </BaseModal>
    );
};

export default EditContactModal;
